import { biText } from '@euler/app/components/message/widgets/bi-visualization/functions/biText';
import { kBiGrainLabels } from '@euler/app/components/message/widgets/bi-visualization/functions/biVizLabels';
import { Typography } from '@euler/app/components/typography';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { BiDatasetCatalog } from '@euler/model/bi/catalog';
import { BiTimeGrain } from '@euler/model/bi/query';
import { BiFieldRole } from '@euler/model/bi/result';
import { memo } from 'react';
import { View } from 'react-native';
import { BiChip } from '../../components/ui/BiChip';
import { EditorSection } from './EditorSection';
import { DimensionCaseSection } from './case/DimensionCaseSection';
import { biFieldLabel } from './functions/biEditorLabels';
import { BiEditorQuery } from './functions/biEditorState';

const kMaxDimensions = 2;

/** What the card groups by; a time field also picks its grain. */
export const DimensionsSection = memo(
  ({
    query,
    catalog,
    onChange,
  }: {
    query: BiEditorQuery;
    catalog: BiDatasetCatalog | null;
    onChange: (next: BiEditorQuery) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const fields =
      catalog?.fields.filter(field => field.role === BiFieldRole.Dimension) ??
      [];
    const listed = new Set(fields.map(field => field.key));
    const selected = (key: string) =>
      query.dimensions.some(ref => ref.field === key);
    const toggle = (key: string, grains: BiTimeGrain[] | undefined) => {
      if (selected(key)) {
        onChange({
          ...query,
          dimensions: query.dimensions.filter(ref => ref.field !== key),
        });
        return;
      }
      if (query.dimensions.length >= kMaxDimensions) return;
      const grain = grains?.length
        ? grains.includes(BiTimeGrain.Day)
          ? BiTimeGrain.Day
          : grains[0]
        : undefined;
      onChange({
        ...query,
        dimensions: [
          ...query.dimensions,
          grain ? { field: key, grain } : { field: key },
        ],
      });
    };
    return (
      <EditorSection title="维度" note="最多两个；第一个作为横轴">
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: rpx(6) }}>
          {fields.map(field => (
            <BiChip
              key={field.key}
              label={biText(field.label)}
              selected={selected(field.key)}
              disabled={
                !selected(field.key) &&
                query.dimensions.length >= kMaxDimensions
              }
              onPress={() => toggle(field.key, field.grains)}
            />
          ))}
          {query.dimensions
            .filter(ref => !listed.has(ref.field))
            .map(ref => (
              <BiChip
                key={ref.field}
                label={`${biFieldLabel(catalog, ref.field)} ×`}
                selected
                onPress={() => toggle(ref.field, undefined)}
              />
            ))}
        </View>
        {catalog
          ? query.dimensions.map((ref, index) => (
              <DimensionCaseSection
                key={`case-${ref.field}-${index}`}
                dimension={ref}
                catalog={catalog}
                onChange={next =>
                  onChange({
                    ...query,
                    dimensions: query.dimensions.map((entry, at) =>
                      at === index ? next : entry,
                    ),
                  })
                }
              />
            ))
          : null}
        {query.dimensions.map(ref => {
          const field = fields.find(entry => entry.key === ref.field);
          if (!field?.grains?.length) return null;
          return (
            <View
              key={ref.field}
              style={{
                flexDirection: 'row',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: rpx(6),
              }}
            >
              <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
                {biText(field.label)}
              </Typography.Text>
              {field.grains.map(grain => (
                <BiChip
                  key={grain}
                  label={kBiGrainLabels[grain]}
                  selected={ref.grain === grain}
                  onPress={() =>
                    onChange({
                      ...query,
                      dimensions: query.dimensions.map(entry =>
                        entry.field === ref.field
                          ? { field: ref.field, grain }
                          : entry,
                      ),
                    })
                  }
                />
              ))}
            </View>
          );
        })}
      </EditorSection>
    );
  },
);
